import React, { useState, useEffect } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatDateTime } from '../utils/dateUtils';

const CaseHistory = () => {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const navigate = useNavigate();

    useEffect(() => {
        fetchCases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, statusFilter, sortField, sortOrder]);

    const fetchCases = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: 30
            };

            // Only add status parameter if it has a value
            if (statusFilter && statusFilter.trim() !== '') {
                params.status = statusFilter;
            }

            const queryString = new URLSearchParams(params).toString();
            const data = await api.get(`/api/cases/?${queryString}`);
            
            // Flatten cases to show each service as a separate row
            const flattenedCases = [];
            data.items.forEach(caseItem => {
                if (caseItem.services && caseItem.services.length > 0) {
                    // If case has services, create a row for each service
                    caseItem.services.forEach(service => {
                        flattenedCases.push({
                            case_id: caseItem.id,
                            case_number: caseItem.case_number,
                            service_id: service.id,
                            service_number: service.service_number,
                            status: caseItem.status,
                            customer_name: caseItem.customer?.name || 'N/A',
                            customer_mobile: caseItem.customer?.mobile_number || 'N/A',
                            vehicle_registration: caseItem.vehicle?.registration_number || 'N/A',
                            scheme_cause_of_call: caseItem.scheme_cause_of_call?.name || 'N/A',
                            total_services: caseItem.total_services,
                            completed_services: caseItem.completed_services,
                            created_at: caseItem.created_at,
                            reported_at: caseItem.reported_at
                        });
                    });
                } else {
                    // If case has no services, create a single row with null service info
                    flattenedCases.push({
                        case_id: caseItem.id,
                        case_number: caseItem.case_number,
                        service_id: null,
                        service_number: 'N/A',
                        status: caseItem.status,
                        customer_name: caseItem.customer?.name || 'N/A',
                        customer_mobile: caseItem.customer?.mobile_number || 'N/A',
                        vehicle_registration: caseItem.vehicle?.registration_number || 'N/A',
                        scheme_cause_of_call: caseItem.scheme_cause_of_call?.name || 'N/A',
                        total_services: caseItem.total_services,
                        completed_services: caseItem.completed_services,
                        created_at: caseItem.created_at,
                        reported_at: caseItem.reported_at
                    });
                }
            });

            // Apply sorting
            const sortedCases = sortData(flattenedCases, sortField, sortOrder);
            
            setCases(sortedCases);
            setTotalPages(data.pages || 1);
            setTotalItems(data.total || 0);
        } catch (error) {
            console.error('Error fetching cases:', error);
            setCases([]);
        } finally {
            setLoading(false);
        }
    };

    const sortData = (data, field, order) => {
        return [...data].sort((a, b) => {
            let aValue = a[field];
            let bValue = b[field];

            // Handle null values
            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            // Handle date fields
            if (field === 'created_at' || field === 'reported_at') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            // Handle numeric fields
            if (field === 'case_id' || field === 'service_id' || field === 'total_services' || field === 'completed_services') {
                aValue = Number(aValue) || 0;
                bValue = Number(bValue) || 0;
            }

            // String comparison
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (order === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            open: 'bg-yellow-100 text-yellow-800',
            in_progress: 'bg-blue-100 text-blue-800',
            resolved: 'bg-green-100 text-green-800',
            closed: 'bg-gray-100 text-gray-800',
            cancelled: 'bg-red-100 text-red-800'
        };

        return (
            <span className={`px-2 py-1 inline-block text-center text-xs w-full leading-5 font-semibold rounded-md ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status ? status.replace(/_/g, ' ').toUpperCase() : 'N/A'}
            </span>
        );
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearchSubmit = () => {
        setCurrentPage(1);
        fetchCases();
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    // Filter cases based on search term
    const filteredCases = cases.filter(caseItem => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        return (
            caseItem.case_number?.toLowerCase().includes(searchLower) ||
            caseItem.service_number?.toLowerCase().includes(searchLower) ||
            caseItem.customer_mobile?.toLowerCase().includes(searchLower) ||
            caseItem.customer_name?.toLowerCase().includes(searchLower)
        );
    });

    const SortableHeader = ({ field, label, currentSortField, currentSortOrder, onSort }) => (
        <th 
            className="px-6 py-4 text-1x1 text-left whitespace-nowrap font-medium text-black cursor-pointer hover:bg-gray-50"
            onClick={() => onSort(field)}
        >
            <div className="flex items-center gap-1">
                {label}
                {currentSortField === field && (
                    <span className="text-gray-400">
                        {currentSortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                )}
            </div>
        </th>
    );

    return (
        <div className="space-y-6">
            <div className="bg-white px-3 bxshadowhd rounded-md py-2 justify-center border items-center mrgall">
                <div className="flex gap-4 pb-2 py-2 mt-0">
                    <h1 className="text-2xl font-semibold text-gray-900">Case History</h1>
                    <div className="flex-1 relative ml-150">
                        <button
                            onClick={handleSearchSubmit}
                            disabled={!searchTerm.trim()}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="w-4 h-4" />
                        </button>
                        <input
                            type="text"
                            placeholder="Search by case number, service number, or customer phone..."
                            value={searchTerm}
                            onChange={handleSearch}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                            className="w-full pl-10 pr-12 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={handleStatusFilter}
                        className="bg-blue-600 text-white px-5 py-2 border border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.5rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5em 1.5em',
                            paddingRight: '2.5rem'
                        }}
                    >
                        <option value="" className="bg-white text-gray-900">All Statuses</option>
                        <option value="open" className="bg-white text-gray-900">Open</option>
                        <option value="in_progress" className="bg-white text-gray-900">In Progress</option>
                        <option value="resolved" className="bg-white text-gray-900">Resolved</option>
                        <option value="closed" className="bg-white text-gray-900">Closed</option>
                        <option value="cancelled" className="bg-white text-gray-900">Cancelled</option>
                    </select>
                    <button
                        onClick={fetchCases}
                        disabled={loading}
                        className="bg-white text-black border px-4 py-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="p-6 bg-white rounded-lg bxshadowhd mrgall border">
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <div className="overflow-x-auto border rounded-md">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <SortableHeader 
                                            field="case_number" 
                                            label="Case Number" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="service_number" 
                                            label="Service Number" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="status" 
                                            label="Status" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="customer_name" 
                                            label="Customer Name" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="customer_mobile" 
                                            label="Customer Mobile" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="vehicle_registration" 
                                            label="Vehicle Registration" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="scheme_cause_of_call" 
                                            label="Cause of Call" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="total_services" 
                                            label="Total Services" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="completed_services" 
                                            label="Completed Services" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                        <SortableHeader 
                                            field="created_at" 
                                            label="Created At" 
                                            currentSortField={sortField} 
                                            currentSortOrder={sortOrder} 
                                            onSort={handleSort} 
                                        />
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredCases.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                                                No cases found
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredCases.map((caseItem, index) => (
                                            <tr key={`${caseItem.case_id}-${caseItem.service_id || index}`} className="hover:bg-gray-50">
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    <button 
                                                        className="text-blue-600 hover:text-blue-800 font-medium underline"
                                                        onClick={() => {
                                                            const url = caseItem.service_id 
                                                                ? `/add-service?case_id=${caseItem.case_id}&service_id=${caseItem.service_id}`
                                                                : `/add-service?case_id=${caseItem.case_id}`;
                                                            navigate(url);
                                                        }}
                                                    >
                                                        {caseItem.case_number}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {caseItem.service_number}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    {getStatusBadge(caseItem.status)}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {caseItem.customer_name}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {caseItem.customer_mobile}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {caseItem.vehicle_registration}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {caseItem.scheme_cause_of_call}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {caseItem.total_services}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {caseItem.completed_services}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {formatDateTime(caseItem.created_at)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="p-4 border-t flex justify-between items-center">
                                <div className="text-sm text-gray-700">
                                    Showing {((currentPage - 1) * 30) + 1} to {Math.min(currentPage * 30, totalItems)} of {totalItems} cases
                                </div>
                                <nav className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CaseHistory; 